/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR GPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE files in the repository root for full details.
*/

import { type URLParams } from "../../vector/url_utils.ts";

/** Name used when opening the SSO login window. Callback identification uses its stored attempt state. */
export const SSO_MODAL_WINDOW_NAME = "muni-sso-login";
/** postMessage type used to relay the OIDC callback from the modal child to the parent. */
export const SSO_MODAL_MESSAGE_TYPE = "muni-sso-callback";

export const SSO_POPUP_STATE_KEY = "mx_muni_sso_popup_state";

const SSO_POPUP_FEATURES = "width=480,height=760,menubar=no,toolbar=no,location=no,status=no";

// Muni: Safari uses redirect until its popup flow is verified end to end.
function isSafari(): boolean {
    const ua = navigator.userAgent;
    return /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR/.test(ua);
}

/**
 * Open the SSO login popup.
 *
 * Must be called synchronously from the click handler that starts login: `window.open` needs
 * transient user activation, and awaiting anything first loses it (immediately on Safari, after a
 * few seconds on Chrome). The window therefore opens blank and is navigated later by
 * {@link startSSOLoginModal} once the authorization URL has been built.
 *
 * @returns the popup window, or null when popups are blocked or unsupported — callers fall back
 *     to the full-page redirect
 */
export function openSsoPopup(): Window | null {
    // Muni: automatic sign-in must use redirect; only a user gesture may open a popup.
    if (isSafari() || navigator.userActivation?.isActive === false) return null;
    try {
        return window.open("", SSO_MODAL_WINDOW_NAME, SSO_POPUP_FEATURES);
    } catch {
        return null;
    }
}

/**
 * When this window is the SSO login modal child, hand the OIDC callback to the window that
 * opened it and stop booting.
 *
 * The child returns to the app's own `redirect_uri`, so without this it would boot a second
 * Element instance. The PKCE `authContext` lives in the parent's `sessionStorage` and is
 * unreachable from here, so only the parent can finish the token exchange.
 *
 * This deliberately has no react-sdk imports: it runs from `vector/index.ts` at the earliest
 * point of boot, before Element acquires its single-session lock. Relaying any later lets the
 * child take the lock from the parent, which surfaces as the "open in another window" prompt.
 *
 * @param params - parsed app URL params; `oauth2` carries the authorization code and state
 * @returns true when the callback was relayed and boot must stop, else false
 */
export function relaySsoCallbackIfChild(params: URLParams): boolean {
    if (!params.oauth2 || !window.opener) return false;
    // Muni: same-origin session storage identifies the callback even if navigation clears window.name.
    const expectedState = sessionStorage.getItem(SSO_POPUP_STATE_KEY);
    if (!expectedState || params.oauth2.state !== expectedState) return false;

    window.opener.postMessage({ type: SSO_MODAL_MESSAGE_TYPE, ...params.oauth2 }, window.location.origin);
    sessionStorage.removeItem(SSO_POPUP_STATE_KEY);
    window.close();
    return true;
}
